import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Search, BookOpen, HelpCircle, Lightbulb, Clock } from 'lucide-react';
import { getAuthToken } from '../utils/api';
import NoteModal from '../components/NoteModal';
import NoteCard from '../components/NoteCard';

const NOTE_TYPES = [
  { value: 'learning', label: 'Learning / Insight', icon: Lightbulb },
  { value: 'question', label: 'Question for Doctor', icon: HelpCircle },
  { value: 'reflection', label: 'Personal Reflection', icon: BookOpen },
  { value: 'revisit', label: 'Something to Revisit', icon: Clock }
];

const LearnNotesPage = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedType, setSelectedType] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  // Fetch notes
  const fetchNotes = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/notes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch notes');
      }
      
      const data = await response.json();
      setNotes(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  // Filter notes
  const filteredNotes = useMemo(() => {
    return notes.filter(note => {
      const matchesType = selectedType === 'All' || note.note_type === selectedType;
      const matchesSearch = 
        (note.title?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
        note.content.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [notes, selectedType, searchQuery]);

  // Handle create new note
  const handleCreateNote = () => {
    setEditingNote(null);
    setIsModalOpen(true);
  };

  // Handle edit note
  const handleEditNote = (note) => {
    setEditingNote(note);
    setIsModalOpen(true);
  };

  // Handle delete note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete note');
      }
      
      // Refresh notes list
      await fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note');
    }
  };

  // Handle modal close and refresh
  const handleModalClose = async (shouldRefresh) => {
    setIsModalOpen(false);
    setEditingNote(null);
    if (shouldRefresh) {
      await fetchNotes();
    }
  };

  // Get counts by type
  const getCountByType = (type) => {
    if (type === 'All') return notes.length;
    return notes.filter(n => n.note_type === type).length;
  };

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link
        to="/integrative-health"
        className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Integrative Health
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Learn & Notes
          </h1>
          <p className="text-lg text-slate-700 leading-relaxed">
            A personal space to record learnings, reflections, and questions as you explore Integrative Health content.
          </p>
        </div>
        <button
          onClick={handleCreateNote}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium shadow-sm whitespace-nowrap min-w-fit"
        >
          <Plus className="h-5 w-5" />
          <span>New Note</span>
        </button>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <p className="text-sm text-slate-700 leading-relaxed">
          Notes are personal reflections and do not constitute medical advice.
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Type Filter */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setSelectedType('All')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            selectedType === 'All'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          All ({notes.length})
        </button>
        {NOTE_TYPES.map(type => {
          const count = getCountByType(type.value);
          return (
            <button
              key={type.value}
              onClick={() => setSelectedType(type.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedType === type.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {type.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Results Count */}
      <div className="text-sm text-slate-600">
        Showing {filteredNotes.length} of {notes.length} notes
        {searchQuery && ` for "${searchQuery}"`}
      </div>

      {/* Notes List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <p className="text-slate-600">Loading notes...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : filteredNotes.length > 0 ? (
          filteredNotes.map(note => (
            <NoteCard
              key={note.id}
              note={note}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
            />
          ))
        ) : (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <p className="text-slate-600">
              {searchQuery || selectedType !== 'All'
                ? 'No notes found matching your criteria.'
                : 'No notes yet. Create your first note to get started!'}
            </p>
          </div>
        )}
      </div>

      {/* Note Modal */}
      {isModalOpen && (
        <NoteModal
          note={editingNote}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default LearnNotesPage;
